import { advance, FunnelMode, Progress, START } from "./funnelProgress";

/*
 * WHO IS A FUNNEL VISITOR, right now, for the spotlight. The numbers and the spotlight must agree, so this uses the SAME rule as the numbers (funnelProgress.ts): a visitor is in the
 * funnel exactly when they entered at step 1 and, in Strict, their path is still unbroken. It is NOT "are they on a funnel page right now": a visitor who landed on /sale and went on to
 * /cart and /checkout is on funnel pages but never entered the funnel, so is not lit (Chris, 2026-10-17: "this session shouldn't have made it into the funnel").
 *
 * A bubble only carries the page it is on, not where it has been, so each visitor's progress is kept here:
 *  - SEEDED from the backend's answer (which knows every open visitor's whole path) each time the numbers arrive, and
 *  - ADVANCED from the bubbles as they change page between answers, so a visitor stepping into the funnel lights up at once, not at the next answer.
 * Each answer re-seeds, so any drift (a missed move, a reconnect) is gone within a second.
 */
export interface OpenState {
  id: string;
  k: number;
  broken: boolean;
  /** the page the visitor was on when the backend counted */
  page: string;
}

export interface Bubble {
  id: string;
  page: string;
}

export interface Members {
  /** the bubbles as they are now: advances anyone who has changed page, starts anyone new, forgets anyone gone */
  observe: (bubbles: Bubble[]) => void;
  /** the backend's view of the open visitors: adopt it (applying any newer page this side has already seen) */
  seed: (open: OpenState[]) => void;
  isLit: (id: string) => boolean;
  /** the whole picture, given from outside (playback knows each replayed visitor's path): adopt it as it is and forget everything else, with nothing advanced from the bubbles */
  replace: (states: OpenState[]) => void;
  /** forget everything (the pages or the mode changed, so every state means something else now) */
  reset: () => void;
  /** for tests */
  stateOf: (id: string) => Progress | undefined;
}

export const createMembers = (getPages: () => string[], getMode: () => FunnelMode): Members => {
  const state = new Map<string, Progress>();
  const last = new Map<string, string>();

  return {
    observe: (bubbles) => {
      const pages = getPages();
      const mode = getMode();
      const present = new Set<string>();
      for (const b of bubbles) {
        present.add(b.id);
        const prev = last.get(b.id);
        if (prev === undefined) {
          if (!state.has(b.id)) state.set(b.id, advance(START, b.page, pages, mode));   // a visitor nobody has told us about: all we know is where they are
        } else if (prev !== b.page) {
          state.set(b.id, advance(state.get(b.id) || START, b.page, pages, mode));
        }
        last.set(b.id, b.page);
      }
      for (const id of Array.from(last.keys())) {
        if (!present.has(id)) {
          last.delete(id);
          state.delete(id);
        }
      }
    },
    seed: (open) => {
      const pages = getPages();
      const mode = getMode();
      for (const o of open) {
        const seen = last.get(o.id);
        const adopted: Progress = { k: o.k, broken: o.broken };
        // this side has already seen the visitor move on from the page the backend counted: apply that move on top, or it would be lost until the next answer
        state.set(o.id, seen !== undefined && seen !== o.page ? advance(adopted, seen, pages, mode) : adopted);
      }
      // a state for a visitor the backend no longer lists and no bubble shows is a leftover
      const listed = new Set(open.map((o) => o.id));
      for (const id of Array.from(state.keys())) if (!listed.has(id) && !last.has(id)) state.delete(id);
    },
    replace: (states) => {
      state.clear();
      last.clear();
      for (const o of states) state.set(o.id, { k: o.k, broken: o.broken });
    },
    isLit: (id) => {
      const s = state.get(id);

      return !!s && s.k >= 1 && !(getMode() === "strict" && s.broken);
    },
    reset: () => {
      state.clear();
      last.clear();
    },
    stateOf: (id) => state.get(id),
  };
};
